import type { Query } from '@feathersjs/feathers';
import rx from 'feathers-reactive';

/**
 * Query keys that are server-side filter flags rather than fields on the
 * records themselves.
 *
 * feathers-reactive's default `siftMatcher` only strips `$`-prefixed operators
 * (e.g. `$limit`, `$sort`) before running `sift` over the query. A plain flag
 * such as `includeHidden: true` therefore becomes a client-side match
 * requirement — and because real records have no `includeHidden` field, every
 * real-time `created` / `patched` / `updated` event fails to match and the row
 * is evicted from the cached result set.
 *
 * That is exactly what broke the loaded-routine subscription: `onGetRoutine`
 * passes `includeHidden: true` (a server filter promoted by `promoteQueryFlags`)
 * so `useRoutineData` drops to `null` the instant the routine is patched — e.g.
 * when the SetVariable widget writes a routine variable — which nulls
 * `currentRoutineId` and kicks the operator off the routine dashboard back to
 * home.
 */
const SERVER_ONLY_QUERY_FLAGS = new Set(['includeHidden']);

/**
 * Drop-in replacement for feathers-reactive's `siftMatcher` that additionally
 * ignores server-only query flags (see {@link SERVER_ONLY_QUERY_FLAGS}) so they
 * never participate in client-side real-time matching. Behaves identically to
 * the default matcher for every other query.
 */
export function reactiveMatcher(originalQuery: Query) {
  const query: Record<string, unknown> = { ...originalQuery };

  for (const key of Object.keys(query)) {
    // `$`-prefixed operators (charCode 36) are pagination/sort directives, not
    // record fields — the default matcher already omits these.
    if (key.charCodeAt(0) === 36 || SERVER_ONLY_QUERY_FLAGS.has(key)) {
      delete query[key];
    }
  }

  return (
    rx as unknown as { sift: (q: unknown) => (item: unknown) => boolean }
  ).sift(query);
}
